import {
  Activity,
  AlertCircle,
  BarChart3,
  Building2,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  DollarSign,
  Download,
  FileText,
  Gift,
  GraduationCap,
  HelpCircle,
  Home,
  Image,
  LayoutTemplate,
  Link2,
  Mail,
  MapPin,
  Megaphone,
  Settings,
  ShieldCheck,
  Truck,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
// src/components/layout/Sidebar.tsx
import { Button, Tooltip } from "react-daisyui";
import { Link, useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { usePermission } from "@/context/PermissionContext";
import api from "@/services/api";

interface SidebarItem {
  badge?: string;
  icon: React.ComponentType<{ className?: string }>;
  id: string;
  path?: string;
  requiredPermission?: string;
  subItems?: SidebarSubItem[];
  title: string;
}

interface SidebarProps {
  className?: string;
  isExpanded: boolean;
  isMobile?: boolean;
  onLinkClick?: () => void;
  onToggle: () => void;
}

interface SidebarSubItem {
  icon: React.ComponentType<{ className?: string }>;
  id: string;
  path: string;
  requiredPermission?: string;
  title: string;
}

export default function Sidebar({
  className = "",
  isExpanded,
  isMobile = false,
  onLinkClick,
  onToggle,
}: SidebarProps) {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { hasPermission } = usePermission();

  const counts = useSidebarCounts();

  const toggleSubmenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId],
    );
  };

  const sidebarItems: SidebarItem[] = [
    {
      icon: Home,
      id: "dashboard",
      path: "/dashboard",
      requiredPermission: "view_dashboard",
      title: "Dashboard",
    },
    {
      badge: counts.reports > 0 ? String(counts.reports) : undefined,
      icon: Mail,
      id: "messages",
      path: "/dashboard/messages",
      requiredPermission: "customer_support",
      title: "Reports",
    },
    {
      badge: counts.applications > 0 ? String(counts.applications) : undefined,
      icon: Truck,
      id: "driver-applications",
      path: "/dashboard/driver-applications",
      requiredPermission: "application_management",
      title: "Applications",
    },
    {
      icon: BarChart3,
      id: "analytics",
      path: "/dashboard/analytics",
      requiredPermission: "analytics_view",
      title: "Analytics",
    },
    {
      icon: Users,
      id: "staff",
      path: "/dashboard/staff",
      requiredPermission: "user_management",
      title: "User Management",
    },
    {
      badge: counts.updates > 0 ? String(counts.updates) : undefined,
      icon: Megaphone,
      id: "updates",
      path: "/dashboard/updates",
      requiredPermission: "updates_management",
      title: "Updates",
    },
    {
      icon: DollarSign,
      id: "finance",
      path: "/dashboard/finance",
      requiredPermission: "financial_data",
      title: "Finance",
    },
    {
      icon: DollarSign,
      id: "wallets",
      path: "/dashboard/wallets",
      requiredPermission: "wallet_management",
      title: "Driver Wallets",
    },
    {
      icon: DollarSign,
      id: "wallet-settings",
      path: "/dashboard/wallet-settings",
      requiredPermission: "wallet_management",
      title: "Wallet Settings",
    },
    {
      icon: Calendar,
      id: "calendar",
      path: "/dashboard/calendar",
      title: "Calendar",
    },
    {
      icon: FileText,
      id: "documents",
      path: "/dashboard/documents",
      requiredPermission: "Documents_management",
      title: "Documents",
    },
    {
      icon: LayoutTemplate,
      id: "landing",
      requiredPermission: "landing_pages_control",
      subItems: [
        {
          icon: Truck,
          id: "catch-rides",
          path: "/dashboard/catch-rides",
          title: "Catch Rides",
        },
        {
          icon: MapPin,
          id: "finding-scooter",
          path: "/dashboard/finding-scooter",
          title: "Finding Scooter",
        },
        {
          icon: Building2,
          id: "restaurant-management",
          path: "/dashboard/restaurant-management",
          title: "Restaurant Content",
        },
        {
          icon: Download,
          id: "app-downloads",
          path: "/dashboard/app-downloads",
          title: "App Downloads",
        },
        {
          icon: Gift,
          id: "hareeng-eats-landing",
          path: "/dashboard/hareeng-eats-landing",
          title: "Hareeng Eats Landing",
        },
        {
          icon: Users,
          id: "about-us-landing",
          path: "/dashboard/about-us-landing",
          title: "About Us Landing",
        },
        {
          icon: GraduationCap,
          id: "careers-landing",
          path: "/dashboard/careers-landing",
          title: "Careers Landing",
        },
        {
          icon: Megaphone,
          id: "community-landing",
          path: "/dashboard/community-landing",
          title: "Community Landing",
        },
        {
          icon: AlertCircle,
          id: "safety-landing",
          path: "/dashboard/safety-landing",
          title: "Safety Landing",
        },
        {
          icon: MapPin,
          id: "cities-landing",
          path: "/dashboard/cities-landing",
          title: "Cities & Airports",
        },
        {
          icon: ShieldCheck,
          id: "auth-demo",
          path: "/dashboard/auth-demo",
          title: "Auth Demo",
        },
        {
          icon: Mail,
          id: "contact-us-landing",
          path: "/dashboard/contact-us-landing",
          title: "Contact Us Landing",
        },
        {
          icon: Image,
          id: "landing-page-hero-backgrounds",
          path: "/dashboard/landing-page-hero-backgrounds",
          title: "Hero Backgrounds",
        },
      ],
      title: "Landing Pages",
    },
    {
      icon: Link2,
      id: "social-links",
      path: "/dashboard/social-links",
      requiredPermission: "social_links",
      title: "Social Links",
    },
    {
      icon: HelpCircle,
      id: "faqs",
      path: "/dashboard/faqs",
      requiredPermission: "FAQs",
      title: "FAQs",
    },
    {
      icon: MapPin,
      id: "zones-and-subzones",
      path: "/dashboard/zones-and-subzones",
      requiredPermission: "zones_&_subzones",
      title: "Zones & Subzones",
    },
    {
      icon: Mail,
      id: "maintenance-subscriptions",
      path: "/dashboard/maintenance-subscriptions",
      requiredPermission: "upcoming_subscriptions",
      title: "Maintenance Subscriptions",
    },
    {
      icon: AlertCircle,
      id: "notifications",
      path: "/dashboard/notifications",
      requiredPermission: "view_notifications",
      title: "Notifications",
    },
    {
      icon: Activity,
      id: "audits",
      path: "/dashboard/audits",
      requiredPermission: "view_audit_trail",
      title: "Audit Trail",
    },
    {
      icon: Settings,
      id: "settings",
      path: "/dashboard/settings",
      title: "Settings",
    },
  ];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const isSubmenuActive = (subItems: SidebarSubItem[]) => {
    return subItems.some((item) => isActive(item.path));
  };

  const renderSubItem = (subItem: SidebarSubItem) => {
    const isItemActive = isActive(subItem.path);
    const IconComponent = subItem.icon;

    return (
      <Link
        className={twMerge(
          "group flex w-full items-center rounded-lg p-2 pl-10 transition-all duration-200",
          "hover:bg-base-200 active:scale-95",
          isItemActive
            ? "border-r-2 border-primary bg-primary/20 text-primary"
            : "text-base-content/70 hover:text-primary",
        )}
        key={subItem.id}
        onClick={onLinkClick}
        to={subItem.path}
      >
        <IconComponent
          className={twMerge(
            "h-4 w-4 transition-transform duration-200",
            "group-hover:scale-110",
            isExpanded && "mr-3",
          )}
        />
        {isExpanded && (
          <span className="flex-1 truncate text-sm font-medium">
            {subItem.title}
          </span>
        )}
      </Link>
    );
  };

  const renderSidebarItem = (item: SidebarItem) => {
    const IconComponent = item.icon;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isSubmenuOpen = expandedMenus.includes(item.id);
    const isItemActive = item.path
      ? isActive(item.path)
      : hasSubItems
        ? isSubmenuActive(item.subItems!)
        : false;

    if (hasSubItems) {
      const buttonContent = (
        <button
          className={twMerge(
            "group flex w-full items-center rounded-lg p-3 transition-all duration-200",
            "hover:bg-base-200 active:scale-95",
            isItemActive
              ? "bg-primary/10 text-primary"
              : "text-base-content hover:text-primary",
            !isExpanded && "justify-center",
          )}
          onClick={() => toggleSubmenu(item.id)}
        >
          <IconComponent
            className={twMerge(
              "h-5 w-5 transition-transform duration-200",
              "group-hover:scale-110",
              isExpanded && "mr-3",
            )}
          />

          {isExpanded && (
            <>
              <span className="flex-1 truncate text-left font-medium">
                {item.title}
              </span>
              {isSubmenuOpen ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
              {item.badge && (
                <span
                  className={twMerge(
                    "ml-2 rounded-full px-2 py-1 text-xs",
                    isItemActive
                      ? "bg-primary text-primary-content"
                      : "bg-primary text-primary-content",
                  )}
                >
                  {item.badge}
                </span>
              )}
            </>
          )}
        </button>
      );

      if (!isExpanded && item.title) {
        return (
          <div className="w-full" key={item.id}>
            <Tooltip message={item.title} position="right">
              <div className="w-full">{buttonContent}</div>
            </Tooltip>
          </div>
        );
      }

      return (
        <div className="w-full" key={item.id}>
          {buttonContent}
          {isExpanded && isSubmenuOpen && (
            <div className="mt-1 space-y-1">
              {item.subItems!.map(renderSubItem)}
            </div>
          )}
        </div>
      );
    }

    // Regular menu item (no submenu)
    const isItemActiveRegular = item.path ? isActive(item.path) : false;

    const buttonContent = (
      <Link
        className={twMerge(
          "group flex w-full items-center rounded-lg p-3 transition-all duration-200",
          "hover:bg-base-200 active:scale-95",
          isItemActiveRegular
            ? "bg-primary text-primary-content shadow-md"
            : "text-base-content hover:text-primary",
          !isExpanded && "justify-center",
        )}
        onClick={onLinkClick}
        to={item.path!}
      >
        <IconComponent
          className={twMerge(
            "h-5 w-5 transition-transform duration-200",
            "group-hover:scale-110",
            isExpanded && "mr-3",
          )}
        />

        {isExpanded && (
          <>
            <span className="flex-1 truncate font-medium">{item.title}</span>
            {item.badge && (
              <span
                className={twMerge(
                  "ml-2 rounded-full px-2 py-1 text-xs",
                  isItemActiveRegular
                    ? "bg-primary-content text-primary"
                    : "bg-primary text-primary-content",
                )}
              >
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );

    if (!isExpanded && item.title) {
      return (
        <Tooltip key={item.id} message={item.title} position="right">
          <div className="w-full">{buttonContent}</div>
        </Tooltip>
      );
    }

    return (
      <div className="w-full" key={item.id}>
        {buttonContent}
      </div>
    );
  };

  return (
    <aside
      className={twMerge(
        "fixed left-0 top-16 h-[calc(100vh-4rem)] border-r border-base-200 bg-base-100",
        "transition-all duration-300 ease-in-out",
        "flex flex-col",
        isMobile
          ? `z-30 w-64 ${isExpanded ? "translate-x-0" : "-translate-x-full"}`
          : `z-10 ${isExpanded ? "w-64" : "w-20"} translate-x-0`,
        className,
      )}
    >
      {/* Sidebar Header */}
      <div className="border-b border-base-200 p-4">
        <div className="flex items-center justify-between">
          {isExpanded && (
            <h2 className="text-lg font-semibold text-base-content">Menu</h2>
          )}
          <Button
            className="hover:bg-base-200"
            color="ghost"
            onClick={onToggle}
            shape="circle"
            size="sm"
          >
            {isExpanded ? (
              <ChevronLeft className="h-10 w-10" />
            ) : (
              <ChevronRight className="h-10 w-10" />
            )}
          </Button>
        </div>
      </div>

      {/* Sidebar Navigation */}
      <nav className="flex-1 space-y-2 overflow-y-auto p-4">
        {sidebarItems
          .filter((item) => {
            // Allow items without required permission
            if (!item.requiredPermission) return true;
            // Show item if user has the required permission
            return hasPermission(item.requiredPermission);
          })
          .map((item) => {
            // If item has subItems, filter those too
            if (item.subItems) {
              const filteredSubItems = item.subItems.filter(
                (subItem) => !subItem.requiredPermission || hasPermission(subItem.requiredPermission),
              );
              // Only show parent if there are visible subItems
              if (filteredSubItems.length === 0) return null;
              return renderSidebarItem({
                ...item,
                subItems: filteredSubItems,
              });
            }
            return renderSidebarItem(item);
          })
          .filter(Boolean)}
      </nav>

      {/* Sidebar Footer */}
      <div className="border-t border-base-200 p-4">
        {isExpanded ? (
          <div className="text-center text-xs text-base-content/50">
            <p>© {new Date().getFullYear()} Hareeng Dashboard</p>
            <p>v1.0.3</p>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
          </div>
        )}
      </div>
    </aside>
  );
}

function useSidebarCounts() {
  const [counts, setCounts] = useState({
    applications: 0,
    reports: 0,
    updates: 0,
  });

  useEffect(() => {
    async function fetchCounts() {
      const [reportsRes, appsRes, updatesRes] = await Promise.all([
        api.get("/reports/open/count"),
        api.get("/applications/pending/count"),
        api.get("/updates/pending-approval/count"),
      ]);
      setCounts({
        applications: appsRes.data.count,
        reports: reportsRes.data.count,
        updates: updatesRes.data.count,
      });
    }
    fetchCounts();
  }, []);

  return counts;
}
